/**
 * Done. CPD — Rules-based CPD plan generator.
 * Ported from Jo's planGenerator.ts. Produces a structured 24-module
 * annual schedule from survey answers — used for calendar sync on the dashboard.
 * The AI narrative plan is separate (stored in annual_plans.plan_text).
 */

import { createCalendarEvent, getEventDate } from './calendar';
import type { SurveyData, CpdModule, StructuredPlan } from './types';

const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];

const REFLECTION_PROMPTS = [
  'What did I do?',
  'What did I learn?',
  'How will this impact my practice?',
];

const FALLBACK_FORMATS = [
  'Short videos','Case studies','Reading articles','Quizzes',
  'Worksheets','Peer discussion','Practice audit','Research summaries','Live webinars',
];

const TOPIC_LIBRARY = [
  { key: 'Geriatric care',                  title: 'Age-Aware Chiropractic Assessment',           topic: 'Geriatric care',              outcome: 'Apply age-aware assessment, consent, mobility, and technique modification decisions for older adults.' },
  { key: 'Balance and falls prevention',    title: 'Falls Risk Screening in Chiropractic Practice', topic: 'Balance and falls prevention', outcome: 'Identify falls risk factors and document practical prevention advice within a chiropractic scope.' },
  { key: 'Pediatric assessment',            title: 'Pediatric Assessment Red Flags and Referral', topic: 'Pediatric assessment',         outcome: 'Improve confidence recognising pediatric red flags, developmental concerns, and referral thresholds.' },
  { key: 'Newborn examinations',            title: 'Newborn Examination Essentials',               topic: 'Newborn care',                 outcome: 'Use a structured, safety-focused approach to newborn history, examination, and parent communication.' },
  { key: 'Primitive reflexes',              title: 'Primitive Reflexes and Developmental Screening', topic: 'Pediatric neurology',        outcome: 'Relate primitive reflex findings to age-appropriate development and referral decision-making.' },
  { key: 'Extremity adjusting',             title: 'Extremity Assessment and Technique Refinement', topic: 'Technique mastery',          outcome: 'Improve clinical reasoning and precision when assessing and managing extremity presentations.' },
  { key: 'Rehabilitation and exercises',    title: 'Rehab Progressions Patients Actually Complete', topic: 'Rehabilitation',             outcome: 'Design simple exercise progressions that support patient adherence and measurable outcomes.' },
  { key: 'Neurology',                       title: 'Practical Neurology for Chiropractors',        topic: 'Neurology',                   outcome: 'Strengthen neurological screening, documentation, and escalation decisions in everyday practice.' },
  { key: 'Imaging interpretation',          title: 'Imaging Review and Referral Decisions',        topic: 'Imaging',                     outcome: 'Review common imaging findings and improve documentation of imaging-related clinical reasoning.' },
  { key: 'Differential diagnosis',          title: 'Differential Diagnosis and Red Flags',         topic: 'Clinical reasoning',          outcome: 'Improve confidence distinguishing mechanical presentations from conditions requiring referral.' },
  { key: 'Chronic pain management',         title: 'Chronic Pain, Communication, and Care Planning', topic: 'Chronic pain',             outcome: 'Use evidence-informed communication and staged goals for patients with persistent pain.' },
  { key: 'Communication/reporting',         title: 'Clear Reports and Patient Explanations',       topic: 'Communication',               outcome: 'Make clinical explanations, reporting, and next-step recommendations clearer and easier to document.' },
  { key: 'Evidence-informed practice',      title: 'Evidence-Informed Chiropractic Updates',       topic: 'Evidence-informed practice',  outcome: 'Connect current evidence with patient-centred chiropractic care decisions.' },
  { key: 'Digital systems and AI',          title: 'Digital Systems and AI for Safer Admin',       topic: 'Digital systems',             outcome: 'Identify low-risk digital workflows that reduce admin load while protecting privacy and professionalism.' },
  { key: 'Staff leadership',                title: 'Team Communication and Clinical Consistency',  topic: 'Leadership',                  outcome: 'Improve team alignment around patient communication, recalls, and professional standards.' },
  { key: 'Business management',             title: 'Sustainable Practice Systems',                 topic: 'Practice management',         outcome: 'Review practice systems that support sustainable, ethical, patient-centred care.' },
  { key: 'Patient retention',               title: 'Retention Through Value and Progress',         topic: 'Patient retention',           outcome: 'Improve patient retention through clearer goals, progress reviews, and informed choice.' },
  { key: 'Cultural competency',             title: 'Cultural Responsiveness in Aotearoa New Zealand', topic: 'Cultural responsiveness',  outcome: 'Reflect on culturally responsive communication, Te Tiriti-informed practice, and inclusive care.' },
  { key: 'Ethics and professional boundaries', title: 'Ethics, Boundaries, and Professional Judgement', topic: 'Ethics',               outcome: 'Apply professional boundaries, advertising, consent, and documentation expectations to real scenarios.' },
];

function list(items: string[], fallback = 'general chiropractic patients'): string {
  if (!items.length) return fallback;
  if (items.length === 1) return (items[0] ?? '').toLowerCase();
  return `${items.slice(0, -1).join(', ').toLowerCase()}, and ${(items[items.length - 1] ?? '').toLowerCase()}`;
}

function pronouns(gender = ''): { subject: string; possessive: string; object: string } {
  if (gender === 'Male')   return { subject: 'He',   possessive: 'His',   object: 'his'   };
  if (gender === 'Female') return { subject: 'She',  possessive: 'Her',   object: 'her'   };
  return                          { subject: 'They', possessive: 'Their', object: 'their' };
}

function yearsPhrase(years = ''): string {
  if (!years) return 'with an evolving level of practice experience';
  if (years === 'Student/New Graduate') return 'as a student or new graduate';
  if (years === '30+') return 'for more than 30 years';
  return `for ${years} years`;
}

function chooseFormat(index: number, answers: SurveyData): string {
  const preferred = (answers.preferredFormats ?? []).filter(f => !(answers.dislikedFormats ?? []).includes(f));
  const pool = preferred.length ? preferred : FALLBACK_FORMATS;
  return pool[index % pool.length] ?? FALLBACK_FORMATS[0] ?? 'Short videos';
}

function minutesPerModule(monthlyTime = ''): number {
  if (monthlyTime === '30 mins') return 25;
  if (monthlyTime === '1 hour')  return 30;
  if (monthlyTime === '2 hours') return 45;
  if (monthlyTime === '3+ hours') return 50;
  return 45;
}

function selectTopics(answers: SurveyData): { title: string; topic: string; outcome: string }[] {
  const confidenceAreas = answers.confidenceAreas ?? [];
  const commonPresentations = answers.commonPresentations ?? [];
  const deepenAreas = answers.deepenAreas ?? [];
  const outcomes = answers.outcomes ?? [];

  const selected = TOPIC_LIBRARY.filter(t => confidenceAreas.includes(t.key));

  const presentationTopics = [
    commonPresentations.includes('Lower back pain')          && { title: 'Low Back Pain Reassessment and Outcomes',         topic: 'Musculoskeletal care',   outcome: 'Use outcome measures and reassessment points to guide low back pain care plans.' },
    commonPresentations.includes('Neck pain/headaches')      && { title: 'Neck Pain, Headache, and Safety Screening',       topic: 'Cervical spine care',    outcome: 'Strengthen screening, consent, and clinical reasoning for neck pain and headache presentations.' },
    commonPresentations.includes('Sports injuries')          && { title: 'Return-to-Activity Decisions for Active Patients', topic: 'Sports chiropractic',   outcome: 'Support staged return-to-activity plans with practical risk and readiness checks.' },
    commonPresentations.includes('Occupational strain injuries') && { title: 'Workload, Ergonomics, and Occupational Strain', topic: 'Occupational health', outcome: 'Give relevant workplace advice and track progress for occupational strain presentations.' },
  ].filter(Boolean) as { title: string; topic: string; outcome: string }[];

  const directionTopics = [
    ...deepenAreas.map(area => ({
      title: `${area} in Chiropractic Practice`,
      topic: area,
      outcome: `Relate ${area.toLowerCase()} to practical, patient-centred chiropractic decisions.`,
    })),
    ...outcomes.slice(0, 3).map(outcome => ({
      title: `${outcome} Action Review`,
      topic: 'Professional direction',
      outcome: `Turn the goal of ${outcome.toLowerCase()} into one practical change in practice.`,
    })),
  ];

  return [
    ...selected,
    ...presentationTopics,
    ...directionTopics,
    { title: 'Cultural Responsiveness in Aotearoa New Zealand', topic: 'Cultural responsiveness', outcome: 'Reflect on equity, cultural safety, and communication choices for New Zealand chiropractic practice.' },
    { title: 'End-of-Year CPD Review and Next Plan',            topic: 'Professional review',     outcome: 'Review evidence, reflections, practice impact, and next year\'s learning priorities.' },
  ];
}

function createProfile(answers: SurveyData): string {
  const p = pronouns(answers.gender);
  const genderPhrase = answers.gender && answers.gender !== 'Prefer not to say'
    ? `${answers.gender.toLowerCase()} chiropractor`
    : 'chiropractor';
  const location = answers.practiceLocation ? `in a ${answers.practiceLocation.toLowerCase()}` : 'in New Zealand';
  const role = answers.role ? `working as a ${answers.role.toLowerCase()}` : 'working in clinical practice';
  return `${answers.ageGroup ? `${answers.ageGroup} ` : ''}${genderPhrase} practising ${yearsPhrase(answers.yearsInPractice)}, ${role} ${location}. ${p.possessive} patient base includes ${list(answers.patientBase ?? [])}, with common presentations including ${list(answers.commonPresentations ?? [], 'spinal, neuromusculoskeletal, and wellness-focused presentations')}. ${p.subject} is seeking stronger confidence in ${list(answers.confidenceAreas ?? [], 'clinical reasoning, communication, and reflective practice')}, while maintaining CPD that is relevant to chiropractic practice in Aotearoa New Zealand and realistic within ${answers.monthlyTime || 'a manageable monthly'} time commitment.`;
}

function createRationale(answers: SurveyData): string {
  const challengeText = answers.weaknesses ? ` The practitioner identified current gaps including: ${answers.weaknesses}.` : '';
  const changeText = answers.demographicChanges ? ` Recent practice changes include: ${answers.demographicChanges}.` : '';
  return `This annual CPD plan is designed to support safe, competent, patient-centred chiropractic practice in New Zealand. The selected activities align with the practitioner's scope, role, patient demographics, common presentations, and stated professional goals. Learning priorities include ${list(answers.confidenceAreas ?? [], 'clinical reasoning and evidence-informed care')}, with additional attention to ${list(answers.deepenAreas ?? [], 'communication, technique, and professional reflection')}. The plan uses varied CPD activities, includes reflection after every module, produces evidence of completion, and supports an end-of-year review suitable for Board-style CPD documentation.${challengeText}${changeText} A cultural responsiveness activity is included to support inclusive care and the expectation that cultural and diversity responsiveness is revisited at least every two years.`;
}

function createObjectives(answers: SurveyData): string[] {
  const primary   = answers.confidenceAreas?.[0] ?? 'clinical reasoning';
  const secondary = answers.confidenceAreas?.[1] ?? answers.commonPresentations?.[0] ?? 'patient communication';
  const format    = answers.preferredFormats?.[0] ?? 'mixed learning activities';
  const motivation = answers.outcomes?.[0] ?? 'better patient outcomes';
  const year = new Date().getFullYear();

  const totalHours = Math.round((minutesPerModule(answers.monthlyTime) * 24) / 60);

  return [
    `Improve confidence in ${primary.toLowerCase()} through completion of targeted chiropractic CPD, reflection notes, and one documented practice change by November ${year}.`,
    `Strengthen management of ${secondary.toLowerCase()} by completing at least four relevant learning activities, recording evidence of completion, and applying one measurable clinical or communication improvement by September ${year}.`,
    `Complete a varied annual CPD programme of approximately ${totalHours} hours using ${format.toLowerCase()} and other activity types, with reflections captured after each module by December ${year}.`,
    `Support ${motivation.toLowerCase()} by reviewing patient communication, documentation, and professional boundaries, then completing an end-of-year review that identifies outcomes, gaps, and next CPD priorities by December ${year}.`,
  ];
}

function createModules(answers: SurveyData): Record<string, CpdModule[]> {
  const topics = selectTopics(answers);
  const moduleMinutes = minutesPerModule(answers.monthlyTime);

  return MONTHS.reduce<Record<string, CpdModule[]>>((acc, month, monthIndex) => {
    acc[month] = [0, 1].map((slot) => {
      const index = monthIndex * 2 + slot;
      const topic = topics[index % topics.length] ?? { title: 'CPD Module', topic: 'General', outcome: 'Complete this CPD module and record a reflection.' };
      const format = chooseFormat(index, answers);
      return {
        id: `${month.toLowerCase()}-${slot + 1}`,
        month,
        title: topic.title,
        topicArea: topic.topic,
        format,
        estimatedMinutes: moduleMinutes,
        intendedOutcome: topic.outcome,
        reflectionPrompts: REFLECTION_PROMPTS,
        evidenceOutput: `${format} completion note, saved resource/certificate where available, and a short Done. CPD reflection.`,
      };
    });
    return acc;
  }, {});
}

export function generateStructuredPlan(answers: SurveyData): StructuredPlan {
  const modulesByMonth = createModules(answers);
  const year = new Date().getFullYear();
  const allModules = Object.values(modulesByMonth).flat();

  const calendarEvents = allModules.map((module, index) => {
    const monthIndex = MONTHS.indexOf(module.month);
    const date = getEventDate(
      year,
      monthIndex,
      index % 2 === 0 ? 1 : 2,
      answers.preferredDay ?? 'Wednesday',
      answers.preferredTime ?? 'Morning',
    );
    return createCalendarEvent(module, date, answers.reminders ?? []);
  });

  return {
    profileSummary: createProfile(answers),
    rationale: createRationale(answers),
    objectives: createObjectives(answers),
    modulesByMonth,
    calendarEvents,
    endOfYearReview: `In December ${year}, review completed modules, saved evidence, reflection notes, patient/practice impact, and remaining gaps. Use this review to set the next annual CPD focus and confirm that cultural or diversity responsiveness remains current within the two-year cycle.`,
  };
}
